import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import GenericModal from "../../../../../Components/Modal/GenericModal";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { Textarea } from "../../../../../ui/textarea";
import { Switch } from "../../../../../ui/switch";
import { Checkbox } from "../../../../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../ui/select";
import { Separator } from "../../../../../ui/separator";
import {
  createBenefitSchema,
  updateBenefitSchema,
  toBenefitPayload,
  benefitToFormValues,
  BENEFIT_CATEGORY_SUGGESTIONS,
  BENEFIT_DELIVERY_METHODS,
  BENEFIT_FOLLOW_UP_ACTIONS,
  BENEFIT_TEMPLATE_FIELDS,
} from "../../schemas";
import {
  createBenefit,
  updateBenefit,
} from "../../../../../Services/adminBenefits";
import BenefitSignalPicker from "./BenefitSignalPicker";
import TemplateField from "./TemplateField";
import { humanEnum, extractApiError } from "./utils";

const DEFAULT_INTRO =
  "Your organization provides ";

const EMPTY_VALUES = {
  name: "",
  provider: "",
  category: "",
  helps_with: "",
  suggested_intro: DEFAULT_INTRO,
  signal_value_ids: [],
  signal_hints: [],
  follow_up_actions: [],
  delivery_methods: [],
  portal_url: "",
  contact_phone: "",
  contact_email: "",
  info_sheet_s3_key: "",
  priority: 100,
  cooldown_days: 14,
  crisis_only: false,
  sms_template: "",
  email_subject_template: "",
  email_body_template: "",
  email_body_is_html: false,
  email_sendgrid_template_id: "",
  inapp_title_template: "",
  inapp_body_template: "",
  inapp_icon: "",
};

const Field = ({ label, htmlFor, error, hint, required, children }) => (
  <div className="grid gap-1.5">
    <Label htmlFor={htmlFor} className="text-sm">
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

const SectionTitle = ({ children, hint }) => (
  <div className="space-y-1">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const HintInput = ({ value = [], onChange }) => {
  const [draft, setDraft] = useState("");
  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setDraft("");
      return;
    }
    if (value.length >= 20) return;
    onChange([...value, trimmed]);
    setDraft("");
  };
  return (
    <div className="grid gap-2">
      <div className="flex gap-2">
        <Input
          placeholder="family medical stress"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          disabled={!draft.trim() || value.length >= 20}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((hint) => (
            <span
              key={hint}
              className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs"
            >
              {hint}
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => v !== hint))}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${hint}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const MultiCheckboxRow = ({ options, value = [], onChange, formatLabel }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-2">
    {options.map((opt) => {
      const checked = value.includes(opt);
      return (
        <label
          key={opt}
          className="flex items-center gap-2 text-sm cursor-pointer select-none"
        >
          <Checkbox
            checked={checked}
            onCheckedChange={(c) =>
              onChange(
                c ? [...value, opt] : value.filter((v) => v !== opt)
              )
            }
          />
          <span>{formatLabel ? formatLabel(opt) : opt}</span>
        </label>
      );
    })}
  </div>
);

/**
 * Modal for creating or editing a corporate-owned benefit.
 * `benefit` null/undefined → create mode.
 * Always lands in draft on create (backend invariant).
 */
const BenefitFormDialog = ({ open, onOpenChange, benefit, onSaved }) => {
  const { id: corporateId } = useParams();
  const isEdit = !!benefit;
  const [submitError, setSubmitError] = useState(null);
  const [signalReloadKey, setSignalReloadKey] = useState(0);
  const [showDeliveryCopy, setShowDeliveryCopy] = useState(false);
  const [emailMode, setEmailMode] = useState(
    benefit?.email_sendgrid_template_id ? "sendgrid" : "custom"
  );

  const defaultValues = useMemo(
    () => (isEdit ? benefitToFormValues(benefit) : EMPTY_VALUES),
    [isEdit, benefit]
  );

  const form = useForm({
    resolver: zodResolver(isEdit ? updateBenefitSchema : createBenefitSchema),
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setSubmitError(null);
      setShowDeliveryCopy(false);
      setEmailMode(
        benefit?.email_sendgrid_template_id ? "sendgrid" : "custom"
      );
    }
  }, [open, defaultValues, reset, benefit]);

  const onSubmit = async (values) => {
    setSubmitError(null);
    try {
      if (isEdit) {
        const patch = toBenefitPayload(values, benefit);
        if (Object.keys(patch).length === 0) {
          onOpenChange?.(false);
          return;
        }
        const updated = await updateBenefit(corporateId, benefit.id, patch);
        onSaved?.(updated, "updated");
      } else {
        const payload = toBenefitPayload(values, null);
        const created = await createBenefit(corporateId, payload);
        onSaved?.(created, "created");
      }
      onOpenChange?.(false);
    } catch (err) {
      const status = err?.response?.status;
      const message = extractApiError(err, "Failed to save benefit.");
      if (status === 409) {
        setError("name", {
          message: "A benefit with this name already exists.",
        });
      } else if (status === 422) {
        // Backend format for template errors:
        //   `sms_template` is not a valid template: <jinja msg> (line N).
        const tplMatch = /`(\w+)` is not a valid template/.exec(message);
        if (tplMatch && BENEFIT_TEMPLATE_FIELDS.includes(tplMatch[1])) {
          setError(tplMatch[1], {
            message: message.replace(/^`\w+` /, ""),
          });
        } else if (/signal/i.test(message)) {
          setError("signal_value_ids", {
            message:
              "One or more selected signals are no longer in your catalog. Please re-select.",
          });
          setSignalReloadKey((k) => k + 1);
        }
      }
      setSubmitError(message);
    }
  };

  const followUps = watch("follow_up_actions") || [];
  const deliveries = watch("delivery_methods") || [];
  const signalIds = watch("signal_value_ids") || [];

  return (
    <GenericModal
      isOpen={open}
      onClose={() => onOpenChange?.(false)}
      heading={isEdit ? "Edit benefit" : "New benefit"}
      primaryLabel=""
      secondaryLabel=""
      maxWidth="820px"
    >
      <div className="w-full text-left">
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? "Update this benefit. Status changes happen separately via Activate / Archive."
            : "Create a benefit. New benefits always land in draft — you'll activate them as a deliberate second step."}
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="grid gap-4 mt-4"
        >
          <div className="grid gap-5 max-h-[55vh] overflow-y-auto px-1 pt-2 pb-1">
          {/* Identity */}
          <div className="grid gap-3">
            <SectionTitle>Identity</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Name"
                htmlFor="ben_name"
                required
                error={errors.name?.message}
              >
                <Input
                  id="ben_name"
                  {...register("name")}
                  placeholder="CareBridge Family Care Support"
                  maxLength={200}
                />
              </Field>
              <Field
                label="Provider"
                htmlFor="ben_provider"
                required
                error={errors.provider?.message}
              >
                <Input
                  id="ben_provider"
                  {...register("provider")}
                  placeholder="CareBridge"
                  maxLength={200}
                />
              </Field>
              <Field
                label="Category"
                htmlFor="ben_category"
                required
                hint="Pick a suggested category or type your own label."
                error={errors.category?.message}
              >
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <div className="grid gap-2">
                      <Select
                        value={
                          BENEFIT_CATEGORY_SUGGESTIONS.includes(field.value)
                            ? field.value
                            : ""
                        }
                        onValueChange={(v) => field.onChange(v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a category…" />
                        </SelectTrigger>
                        <SelectContent>
                          {BENEFIT_CATEGORY_SUGGESTIONS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {humanEnum(c)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Or type a custom label"
                        maxLength={80}
                      />
                    </div>
                  )}
                />
              </Field>
              <Field
                label="Priority"
                htmlFor="ben_priority"
                hint="Tiebreaker priority — used only to break ties between multiple matching benefits. Lower wins."
                error={errors.priority?.message}
              >
                <Input
                  id="ben_priority"
                  type="number"
                  min={0}
                  {...register("priority")}
                />
              </Field>
            </div>
          </div>

          <Separator />

          {/* Copy */}
          <div className="grid gap-3">
            <SectionTitle hint="The AI paraphrases these — they shape how ARIS will mention the benefit, never quoted verbatim.">
              Copy
            </SectionTitle>
            <Field
              label="Helps with"
              htmlFor="ben_helps_with"
              required
              hint="Plain-English summary of what this benefit covers."
              error={errors.helps_with?.message}
            >
              <Textarea
                id="ben_helps_with"
                rows={3}
                maxLength={2000}
                placeholder="Eldercare planning, caregiver coaching, respite care."
                {...register("helps_with")}
              />
            </Field>
            <Field
              label="Suggested intro"
              htmlFor="ben_intro"
              required
              hint='Reference sentence used as a tone anchor. Always frame as "your organization provides X" — never as "the company is paying for X".'
              error={errors.suggested_intro?.message}
            >
              <Textarea
                id="ben_intro"
                rows={3}
                maxLength={2000}
                placeholder="Your organization provides caregiving support…"
                {...register("suggested_intro")}
              />
            </Field>
          </div>

          <Separator />

          {/* Signals */}
          <div className="grid gap-3">
            <SectionTitle hint="Signals are the topics ARIS listens for. Hints are soft, secondary matches.">
              Targeting
            </SectionTitle>
            <Field
              label="Signals"
              hint="Manage the catalog via the Signal catalog panel."
              error={errors.signal_value_ids?.message}
            >
              <Controller
                control={control}
                name="signal_value_ids"
                render={({ field }) => (
                  <BenefitSignalPicker
                    corporateId={corporateId}
                    value={field.value || []}
                    onChange={field.onChange}
                    reloadKey={signalReloadKey}
                  />
                )}
              />
            </Field>
            <Field
              label="Signal hints"
              hint="Free-form phrases — soft signals only. Up to 20."
              error={errors.signal_hints?.message}
            >
              <Controller
                control={control}
                name="signal_hints"
                render={({ field }) => (
                  <HintInput value={field.value || []} onChange={field.onChange} />
                )}
              />
            </Field>
          </div>

          <Separator />

          {/* Delivery & contact */}
          <div className="grid gap-3">
            <SectionTitle>Delivery & contact</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Delivery methods" hint="How ARIS can hand off this benefit.">
                <MultiCheckboxRow
                  options={BENEFIT_DELIVERY_METHODS}
                  value={deliveries}
                  onChange={(next) =>
                    setValue("delivery_methods", next, { shouldDirty: true })
                  }
                  formatLabel={humanEnum}
                />
              </Field>
              <Field label="Follow-up actions" hint="What ARIS may offer after surfacing.">
                <MultiCheckboxRow
                  options={BENEFIT_FOLLOW_UP_ACTIONS}
                  value={followUps}
                  onChange={(next) =>
                    setValue("follow_up_actions", next, { shouldDirty: true })
                  }
                  formatLabel={humanEnum}
                />
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Portal URL"
                htmlFor="ben_portal"
                hint="https only."
                error={errors.portal_url?.message}
              >
                <Input
                  id="ben_portal"
                  type="url"
                  placeholder="https://example.com/care"
                  {...register("portal_url")}
                />
              </Field>
              <Field
                label="Contact phone"
                htmlFor="ben_phone"
                hint="E.164 recommended."
                error={errors.contact_phone?.message}
              >
                <Input
                  id="ben_phone"
                  placeholder="+1-555-0100"
                  {...register("contact_phone")}
                />
              </Field>
              <Field
                label="Contact email"
                htmlFor="ben_email"
                error={errors.contact_email?.message}
              >
                <Input
                  id="ben_email"
                  type="email"
                  placeholder="care@example.com"
                  {...register("contact_email")}
                />
              </Field>
              <Field
                label="Info sheet (S3 key)"
                htmlFor="ben_info_sheet"
                hint="Optional pre-uploaded info-sheet object key."
                error={errors.info_sheet_s3_key?.message}
              >
                <Input
                  id="ben_info_sheet"
                  placeholder="benefits/carebridge.pdf"
                  {...register("info_sheet_s3_key")}
                />
              </Field>
            </div>
          </div>

          <Separator />

          {/* Advanced */}
          <div className="grid gap-3">
            <SectionTitle>Advanced</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4 items-start">
              <Field
                label="Cooldown (days)"
                htmlFor="ben_cooldown"
                hint="Don't re-surface this benefit to the same member within N days."
                error={errors.cooldown_days?.message}
              >
                <Input
                  id="ben_cooldown"
                  type="number"
                  min={0}
                  {...register("cooldown_days")}
                />
              </Field>
              <Field label="Crisis only">
                <Controller
                  control={control}
                  name="crisis_only"
                  render={({ field }) => (
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                      <span className="text-sm">
                        {field.value
                          ? "Only during crisis containment"
                          : "Surfaced anytime signals match"}
                      </span>
                    </div>
                  )}
                />
              </Field>
            </div>
          </div>

          <Separator />

          {/* Delivery copy — admin-authored Jinja2 templates per channel */}
          <div className="grid gap-3">
            <button
              type="button"
              onClick={() => setShowDeliveryCopy((v) => !v)}
              className="flex items-center justify-between text-left"
            >
              <SectionTitle hint="Optional per-channel message templates. Leave blank to use the auto-composed copy.">
                Delivery copy
              </SectionTitle>
              <span className="text-xs text-muted-foreground">
                {showDeliveryCopy ? "Hide" : "Show"}
              </span>
            </button>

            {showDeliveryCopy && (
              <div className="grid gap-5 rounded-md border bg-muted/20 p-4">
                {deliveries.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Pick at least one delivery method above to see per-channel
                    template fields.
                  </p>
                )}

                {deliveries.includes("sms") && (
                  <div className="grid gap-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      SMS
                    </h4>
                    <Controller
                      control={control}
                      name="sms_template"
                      render={({ field, fieldState }) => (
                        <TemplateField
                          field={field}
                          label="SMS body"
                          maxLength={600}
                          multiline
                          rows={3}
                          placeholder="Hi {{ member.first_name }} — {{ corporate.display_name }} provides {{ benefit.name }}. Learn more: {{ benefit.portal_url }}"
                          hint="Plain text. Max 600 characters."
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                )}

                {deliveries.includes("email") && (
                  <div className="grid gap-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Email
                    </h4>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="email_mode"
                          value="custom"
                          checked={emailMode === "custom"}
                          onChange={() => setEmailMode("custom")}
                        />
                        <span>Custom HTML / text body</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="email_mode"
                          value="sendgrid"
                          checked={emailMode === "sendgrid"}
                          onChange={() => setEmailMode("sendgrid")}
                        />
                        <span>SendGrid dynamic template</span>
                      </label>
                    </div>

                    {emailMode === "custom" ? (
                      <>
                        <Controller
                          control={control}
                          name="email_subject_template"
                          render={({ field, fieldState }) => (
                            <TemplateField
                              field={field}
                              label="Subject"
                              maxLength={300}
                              placeholder="{{ corporate.display_name }} offers {{ benefit.name }}"
                              hint="Single line. Max 300 characters."
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="email_body_template"
                          render={({ field, fieldState }) => (
                            <TemplateField
                              field={field}
                              label="Body"
                              maxLength={10000}
                              multiline
                              rows={6}
                              placeholder="Hi {{ member.first_name }},\n\n{{ corporate.display_name }} provides {{ benefit.name }} ..."
                              hint="Max 10000 characters. Mark as HTML below if it contains markup."
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="email_body_is_html"
                          render={({ field }) => (
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox
                                checked={!!field.value}
                                onCheckedChange={field.onChange}
                              />
                              <span>Body is HTML</span>
                            </label>
                          )}
                        />
                      </>
                    ) : (
                      <Controller
                        control={control}
                        name="email_sendgrid_template_id"
                        render={({ field, fieldState }) => (
                          <div className="grid gap-1.5">
                            <Label htmlFor="email_sendgrid_template_id">
                              SendGrid template ID
                            </Label>
                            <Input
                              id="email_sendgrid_template_id"
                              placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                              maxLength={64}
                              {...field}
                              value={field.value || ""}
                            />
                            <p className="text-xs text-muted-foreground">
                              When set, the subject/body fields are ignored at
                              send-time; SendGrid renders the dynamic template
                              with the same variable context.
                            </p>
                            {fieldState.error?.message && (
                              <p className="text-xs text-destructive">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    )}
                  </div>
                )}

                {deliveries.includes("in_app") && (
                  <div className="grid gap-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      In-app
                    </h4>
                    <Controller
                      control={control}
                      name="inapp_title_template"
                      render={({ field, fieldState }) => (
                        <TemplateField
                          field={field}
                          label="Title"
                          maxLength={200}
                          placeholder="{{ benefit.name }}"
                          hint="Short headline. Max 200 characters."
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="inapp_body_template"
                      render={({ field, fieldState }) => (
                        <TemplateField
                          field={field}
                          label="Body"
                          maxLength={2000}
                          multiline
                          rows={3}
                          placeholder="{{ benefit.helps_with }}"
                          hint="Plain text. Max 2000 characters."
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="inapp_icon"
                      render={({ field, fieldState }) => (
                        <div className="grid gap-1.5">
                          <Label htmlFor="inapp_icon">Icon</Label>
                          <Input
                            id="inapp_icon"
                            placeholder="benefits_icon"
                            maxLength={80}
                            {...field}
                            value={field.value || ""}
                          />
                          <p className="text-xs text-muted-foreground">
                            Matches notification_mapper.yaml naming (e.g.{" "}
                            <code className="text-[11px]">benefits_icon</code>,{" "}
                            <code className="text-[11px]">caregiving_icon</code>
                            ). Mobile resolves to a local asset.
                          </p>
                          {fieldState.error?.message && (
                            <p className="text-xs text-destructive">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving…"
                : isEdit
                ? "Save changes"
                : "Create draft"}
            </Button>
          </div>
        </form>
      </div>
    </GenericModal>
  );
};

export default BenefitFormDialog;
